// @flow

import * as React from "react"
import {connect} from "react-redux"
import Modal from "react-modal"
import ClassNames from "classnames"
import {withTranslation} from "react-i18next"

import Config from "../config"
import ConfirmModal from "./modals/confirm"
import {updateBabyState} from "../actions/babies"
import {hasState} from "../reducers/baby"
import type {Baby, Dispatch} from "../types"

type Props = {
  baby: Baby,
  isStandalone: boolean,
  onPlanIntake: () => void,
  onBabyBorn: () => void,
  dispatch: Dispatch,
  t: any,
  latestOfferDeposit?: string,
  currentDeposit?: string,
}

type ModalType = "sign" | "evaluation_taken"

type State = {
  +isModalOpen: boolean,
  +modalType: ?ModalType,
}

const URL = Config.baseUrl

class BabyCardActions extends React.Component<Props, State> {
  static defaultProps = {
    isStandalone: false,
    onPlanIntake: () => {},
    latestOfferDeposit: "0",
    currentDeposit: "0",
  }

  handleSignClick: () => void
  handleEvaluationTakenClick: () => void
  handleConfirmSignModal: () => void
  handleConfirmEvaluationTakenModal: () => void
  handleCancelModal: () => void

  state = {
    isModalOpen: false,
    modalType: null,
  }

  constructor(props: Props) {
    super(props)

    Modal.setAppElement("#app")

    this.handleSignClick = this.handleSignClick.bind(this)
    this.handleEvaluationTakenClick = this.handleEvaluationTakenClick.bind(this)
    this.handleConfirmSignModal = this.handleConfirmSignModal.bind(this)
    this.handleConfirmEvaluationTakenModal =
      this.handleConfirmEvaluationTakenModal.bind(this)
    this.handleCancelModal = this.handleCancelModal.bind(this)
  }

  handleSignClick() {
    this.setState({isModalOpen: true, modalType: "sign"})
  }

  handleEvaluationTakenClick() {
    this.setState({isModalOpen: true, modalType: "evaluation_taken"})
  }

  handleConfirmSignModal() {
    const {baby, dispatch, isStandalone} = this.props
    dispatch(updateBabyState(baby.token, "signed"))
    if (isStandalone) setTimeout(() => window.location.reload(), 250)
    this.setState({isModalOpen: false})
  }

  handleConfirmEvaluationTakenModal() {
    const {baby, dispatch, isStandalone} = this.props
    dispatch(updateBabyState(baby.token, "evaluation_taken"))
    if (isStandalone) setTimeout(() => window.location.reload(), 250)
    this.setState({isModalOpen: false})
  }

  handleCancelModal() {
    this.setState({isModalOpen: false})
  }

  renderActions() {
    const {baby, t, onPlanIntake, onBabyBorn, isStandalone} = this.props
    const leftActions = []
    const isBorn = baby.birthDate !== null && hasState(baby, "born")

    const btnClass = ClassNames("btn", {"btn-primary": isStandalone})

    if (baby.state == "new" || baby.state == "prospect") {
      leftActions.push(
        <span className="badge badge-warning mr-2" key="new">
          new
        </span>
      )
    }

    if (
      !hasState(baby, "intake_planned") &&
      !hasState(baby, "intake_done") &&
      hasState(baby, "dispatched")
    ) {
      leftActions.push(
        <button
          key="plan_intake"
          className={btnClass}
          type="button"
          onClick={onPlanIntake}
        >
          {t("babyCard.actions.planIntake")}
        </button>
      )
    }

    if (baby.state == "confirmed" && isStandalone) {
      leftActions.push(
        <span className="badge badge-info mr-2" key="confirmed">
          confirmed
        </span>
      )
    }

    if (
      hasState(baby, "intake_planned") &&
      !hasState(baby, "evaluation_sent") &&
      !isBorn
    ) {
      leftActions.push(
        <a
          href={`${URL}/intake/${baby.token}`}
          key="intake"
          className={btnClass}
        >
          {t("babyCard.actions.intake")}
        </a>
      )
    }

    if (hasState(baby, "intake_done") && !isBorn) {
      leftActions.push(
        <button
          className={btnClass}
          type="button"
          key="born"
          onClick={onBabyBorn}
        >
          {t("babyCard.actions.born")}
        </button>
      )
    }

    if (
      hasState(baby, "intake_done") &&
      (hasState(baby, "signed") || hasState(baby, "partially_signed")) &&
      !hasState(baby, "evaluation_sent") &&
      !isBorn
    ) {
      leftActions.push(
        <a
          href={`${URL}/admin/babies/${baby.id}/products`}
          className={btnClass}
          key="search-items"
        >
          {t("babyCard.actions.searchItems")}
        </a>
      )
    }

    if (
      hasState(baby, "intake_done") &&
      !hasState(baby, "signed") &&
      !hasState(baby, "evaluation_sent")
    ) {
      leftActions.push(
        <button
          className={btnClass}
          type="button"
          key="sign"
          onClick={this.handleSignClick}
        >
          {t("babyCard.actions.sign")}
        </button>
      )
    }

    if (
      (hasState(baby, "evaluation_sent") || isBorn) &&
      !hasState(baby, "evaluation_taken")
    ) {
      leftActions.push(
        <button
          className={btnClass}
          type="button"
          key="evaluation_taken"
          onClick={this.handleEvaluationTakenClick}
        >
          {t("babyCard.actions.evaluationTaken")}
        </button>
      )
    }

    if (baby.state == "lost" && isStandalone) {
      leftActions.push(
        <span className="badge badge-danger mx-1" key="lost">
          lost
        </span>
      )
    }

    return <div className="l">{leftActions}</div>
  }

  renderModal() {
    const {isModalOpen, modalType} = this.state
    if (!isModalOpen) return null
    switch (modalType) {
      case "sign":
        let {latestOfferDeposit, currentDeposit, t} = this.props
        latestOfferDeposit = parseFloat(latestOfferDeposit)
        currentDeposit = parseFloat(currentDeposit)

        if (!isNaN(latestOfferDeposit) && !isNaN(currentDeposit)) {
          let message = null
          if (latestOfferDeposit != currentDeposit) {
            const link = window.location.href + "/invoicing"

            message = t("babyCard.actions.depositDiff", {
              latestOfferDeposit,
              currentDeposit,
              link,
            })
          }
          return (
            <ConfirmModal
              onConfirm={this.handleConfirmSignModal}
              message={message}
              onCancel={this.handleCancelModal}
            />
          )
        } else {
          return (
            <ConfirmModal
              onConfirm={this.handleConfirmSignModal}
              onCancel={this.handleCancelModal}
            />
          )
        }
      case "evaluation_taken":
        return (
          <ConfirmModal
            onConfirm={this.handleConfirmEvaluationTakenModal}
            onCancel={this.handleCancelModal}
          />
        )
    }
  }

  render() {
    const {t, isStandalone, baby} = this.props
    const className = ClassNames("baby-card-actions", {
      "is-standalone": isStandalone,
    })
    return (
      <div className={className}>
        {this.renderActions()}
        {this.renderModal()}
        {!isStandalone ? (
          <div className="r">
            <a className="btn" href={`${URL}/admin/babies/${baby.id}`}>
              {t("babyCard.actions.details")}
            </a>
          </div>
        ) : null}
      </div>
    )
  }
}

export default withTranslation()(connect()(BabyCardActions))
